/** @jsx isolateComponent */
import React, { useEffect } from 'react';
import { css } from '@emotion/core';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import {
  Typeahead,
  FloatingMenu,
  FloatingMenuProps,
} from '@zapier/design-system';

import { useAppSearchData } from './useAppSearchData';
import type { UseQuery } from '../../types';
import { ServiceIcon } from '../ServiceIcon';

const Styles = labelStyles('ServiceTypeahead', {
  serviceIcon: css`
    &[data-disabled] {
      opacity: 0.6;
    }
  `,
});

type ServiceTypeaheadProps = {
  /**
   * Accessibility label that describes this component. This should be supplied
   * if no corresponding <label> element is used.
   */
  ariaLabel?: string;
  /**
   * The text that renders in a `Tooltip` when the input `isDisabled`.
   * Only renders when `isDisabled`.
   */
  disabledText?: string;
  /**
   * HTML `id` to use for the node.
   */
  id?: string;
  /**
   * Optional prop for setting the initially selected item
   */
  initialSelectedItem?: any;
  /**
   * HTML `id` attribute for the `input` element. This prop is named `inputId`
   * instead of `id` for consistency with the Downshift API.
   */
  inputId?: string;
  /**
   * Disables the input and makes it non-interactive.
   */
  isDisabled?: boolean;
  /**
   * `id` of the label that connects to this select field.
   */
  labelId?: string;

  /**
   *  The total number of results to show in the dropdown
   */
  resultsLimit?: number;

  /**
   * Accessibility label that describes the options available inside the select menu, e.g. "List of items", "List of Countries", etc.
   */
  menuAriaLabel: string;
  /**
   * Callback that gets called when the user selects an item and the selected item has changed.
   */
  onChange: (item: any) => void;
  /**
   * Callback that gets called when service search results are updated.
   */
  onResultsUpdate?: (searchQuery: string, results: any) => void;
  /**
   * Placeholder to render inside the select toggle when no item is selected.
   */
  placeholder: string;
  /**
   * The rendered size of the input.
   */
  size?: 'small' | 'medium';
  /**
   * Optional app slug that when provided will filter that app out of the
   * search results. Used when you are searching within a specific context,
   * e.g. 1 app integration pages and you're within the context of Tortii, we
   * don't want Tortii to appear in search results for connecting to another
   * app.
   */
  slugFilter?: string;
  /** Excludes upcoming apps from search results. */
  excludeUpcoming?: boolean;
  /**
   * An optional function to hold an external reference to Downshift's clearSelection prop
   */
  setClearSelection?: (f: Function) => Function;
  /**
   * Hook to actually execute the query. Typically provided by an `@apollo` package.
   */
  useQuery: UseQuery;
  /**
   * A render prop for creating the floating menu.
   */
  renderFloatingMenu: (props: FloatingMenuProps) => React.ReactElement;
};

const emptyItem = {
  disabled: true,
  name: 'No search results found.',
};

/**
 * A custom version of `Typeahead` which is intended for searching for and selecting a service. Renders icons appropriate for each service.
 */
export const ServiceTypeahead = (props: ServiceTypeaheadProps) => {
  const {
    ariaLabel,
    disabledText,
    initialSelectedItem,
    inputId,
    isDisabled,
    labelId,
    menuAriaLabel,
    onChange,
    onResultsUpdate,
    placeholder,
    slugFilter,
    excludeUpcoming,
    resultsLimit,
    setClearSelection,
    size,
  } = props;
  const getIconUrlForItem = (item: any) => item.images.url_32x32;
  const getLabelForItem = (item: any) => item.name;

  const renderIcon = (item: any) => {
    if (!item || !item.images) {
      return null;
    }

    return (
      <span
        css={Styles.serviceIcon}
        data-disabled={isDisabled ? '' : undefined}
      >
        <ServiceIcon
          ariaHidden={true}
          serviceName={getLabelForItem(item)}
          size={30}
          src={getIconUrlForItem(item)}
        />
      </span>
    );
  };

  const { apps, loading, onSearch, searchQuery } = useAppSearchData({
    limit: resultsLimit,
    searchDebounceDelay: 300,
    excludeUpcoming,
    useQuery: props.useQuery,
  });

  useEffect(() => {
    if (!loading && searchQuery.length && onResultsUpdate) {
      onResultsUpdate(searchQuery, apps);
    }
  }, [apps, loading, onResultsUpdate, searchQuery]);

  const filteredApps = !slugFilter
    ? apps
    : apps.filter((app: any) => app.slug !== slugFilter);

  const hasZeroResults =
    loading === false && searchQuery.length > 0 && filteredApps.length === 0;

  const displayedApps = hasZeroResults ? [emptyItem] : filteredApps;

  return (
    <Typeahead
      ariaLabel={ariaLabel}
      autoComplete="off"
      disabledText={disabledText}
      getLabelForItem={getLabelForItem}
      id={props.id}
      initialSelectedItem={initialSelectedItem}
      inputId={inputId}
      isBusy={loading}
      isDisabled={isDisabled}
      // We are intentionally passing an empty `items` while loading previous
      // search results while searching. This prevents the user from clicking or
      // tapping on a result right after results suddenly change, which is a very
      // frustraing UX.
      //
      // Also passing empty `items` when the `searchQuery` is blank. This is
      // because `AppSearchBackend` returns a default set of apps when a
      // blank search query is specified. When the user type an initial
      // character into the `Typeahead`, these default apps render for a
      // split second, creating the impression of poor search results until
      // the GraphQL query returns.
      items={loading || searchQuery === '' ? [] : displayedApps}
      labelId={labelId}
      menuAriaLabel={menuAriaLabel}
      onChange={onChange}
      onInputValueChange={(query: string) => onSearch(query)}
      placeholder={placeholder}
      renderFloatingMenu={(menuProps) =>
        props.renderFloatingMenu({ ...menuProps, visibleItems: resultsLimit })
      }
      renderIcon={renderIcon}
      setClearSelection={setClearSelection}
      showEmptyList={true}
      size={size}
    />
  );
};

ServiceTypeahead.defaultProps = {
  excludeUpcoming: true,
  onResultsUpdate: () => {},
  renderFloatingMenu: (props: FloatingMenuProps) => <FloatingMenu {...props} />,
  resultsLimit: 4,
};
